import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { isValidTitle } from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import {
  AvatarCell,
  AvatarSize,
  Button,
  ButtonColor,
  ButtonVariant,
  Field,
  Label,
  Modal,
  SelectDropdown,
  SimpleInput,
} from '@makyoapp/makyo-ui'

import { appConfig } from '@/configs'
import { DbItemAdapterV2 } from '@/adapters/DbAdapter'
import { useGetOrgDatabases } from '@/resolvers/db/query'
import { useGetOrgApps } from '@/resolvers/org/apps/query'
import { useGetOrgTeams } from '@/resolvers/org/query'
import { useGetMyTrashList } from '@/resolvers/user/query'
import { useCreateDbContext } from '@/context/CreateDbContext'
import { useOrgAppsContext } from '@/context/OrgAppsContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useFormEnter } from '@/shared/makyo-hooks'
import { cleanUpString, debounce } from '@/shared/utils'
import { CreateDbModal } from '@/pages/org/databases/OrgDetailCreateDb'
import { DbDropdown } from '@/components/DbDropdown'
import { SaveButton } from '@/components/SaveButton'

const newProjectFormId = 'org-detail-new-app-form'

type onAddType = (name: string, team: string, dbId: string, closeModal: () => void) => void

interface AddNewAppModalProps {
  onAdd: (name: string, team: string, dbId: string, closeModal: () => void) => void
  hideButton?: boolean
  databases?: DbItemAdapterV2[]
}

function AddNewAppModal({ onAdd, databases }: AddNewAppModalProps) {
  const { t } = useTranslation()

  // Get App List from SDK
  const { getAppFilter } = useOrgAppsContext()
  const orgAppsQuery = useGetOrgApps({ params: getAppFilter() })
  const myTrashQuery = useGetMyTrashList()

  const isLoadingApps = orgAppsQuery.isLoading || myTrashQuery.isLoading

  const { setForcedTeams, reset } = useCreateDbContext()
  const { data: orgTeams } = useGetOrgTeams()

  const [newProjectName, setNewProjectName] = useState<string>('')
  const [team, setTeam] = useState(orgTeams?.[0]?.id || '')
  const [isLoading, setIsLoading] = useState(false)
  const [nameError, setNameError] = useState<string | undefined>(undefined)

  useEffect(() => {
    const ft = orgTeams?.find((tm) => tm.id === team)
    if (ft) {
      setForcedTeams([ft])
    }
  }, [team, setForcedTeams, orgTeams])

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null

    if (!!newProjectName) {
      if (timeout) {
        clearTimeout(timeout)
      }
      timeout = setTimeout(() => {
        setNewProjectName(cleanUpString(newProjectName))
      }, 1000)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [newProjectName])

  const [step, setStep] = useState(1)

  const { showAdd, setShowAdd } = useOrgAppsContext()

  const filteredDb = useMemo(() => {
    return databases
      ?.filter((d) => {
        return d.teams.find((tm) => tm.id === team)
      })
      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()) // Sort by creation date in descending order
  }, [databases, team])

  const defaultDb = filteredDb?.[0]

  const [db, setDb] = useState<DbItemAdapterV2 | undefined>(defaultDb)

  useEffect(() => {
    if (!!filteredDb?.length) {
      setDb(filteredDb?.[0])
    } else {
      setDb(undefined)
    }
  }, [filteredDb, filteredDb?.length])

  const [existingDb, setExistingDb] = useState(false)

  const canCreateDb = useOrgPerm(OrgPermission.DBCreateLinkDatabasesAndTables)

  const closeModal = useCallback(() => {
    setNewProjectName('') // We clean new project name
    setTeam(orgTeams?.[0].id || '')
    setShowAdd(false) // We close the modal
    setIsLoading(false)
    setExistingDb(false)
    setStep(1)
  }, [setNewProjectName, setTeam, orgTeams, setShowAdd, setIsLoading, setExistingDb, setStep])

  // handle creates a new project
  const handleEnter = useCallback(() => {
    if (step === 1) {
      if (!databases || databases.length < 1) {
        setExistingDb(false)
        setStep((prev) => prev + 2)
      } else {
        setStep((prev) => prev + 1)
      }
    }
  }, [step, setStep, setExistingDb, databases])

  const handleCreate = useCallback(
    async (onAdd: onAddType, dbId?: string) => {
      if (step > 1) {
        setIsLoading(true)
        onAdd(newProjectName, team, dbId ? dbId : (db?.id ?? ''), closeModal)
      }
    },
    [step, db, team, newProjectName, closeModal, setIsLoading]
  )

  useFormEnter(newProjectFormId, handleEnter)

  const nameCharCount = newProjectName.trim().length
  const validLength = nameCharCount >= appConfig.charConstraints.min && nameCharCount < appConfig.charConstraints.max

  const stepOneNextButtonDisabled = !validLength || !!nameError

  const onCreateDb = () => {
    setExistingDb(false)
    setStep((prev) => prev + 1)
  }

  const handleStepClick = () => {
    if (!databases || databases.length < 1) {
      if (step === 3) {
        setStep((prev) => prev - 2)
      } else {
        setStep((prev) => prev - 1)
      }
    } else {
      setStep((prev) => prev - 1)
    }
    setExistingDb(false)
    reset()
  }

  const debouncedValidateName = debounce(async (name: string) => {
    const existingApp = orgAppsQuery.data?.some((app) => app.title.toLowerCase() === name.toLowerCase())
    const existingTrash = myTrashQuery.data?.some(
      (item) => item._ItemType === 'project' && item._ItemName.toLowerCase() === name.toLowerCase()
    )

    switch (true) {
      case !isValidTitle(name):
        setNameError(t('Please enter a valid name'))
        break
      case existingApp:
        setNameError(t('App name already exists'))
        break
      case existingTrash:
        setNameError(t('App name already exists in trash'))
        break
      default:
        setNameError(undefined)
        break
    }
  }, 500)

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewProjectName(e.currentTarget.value)
    debouncedValidateName(e.currentTarget.value)
  }

  if (!orgTeams) return null

  if (isLoadingApps) {
    return (
      <Modal id='org-detail-new-app-modal' show={showAdd} onHide={closeModal}>
        <Modal.Header onCloseClick={closeModal}>{t('Add New App')}</Modal.Header>
        <Modal.Body className='modal-md flex-cc min-h-[200px]'>
          <span className='loading' />
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal id='org-detail-new-app-modal' show={showAdd} onHide={closeModal}>
      <Modal.Header onCloseClick={closeModal}>{t('Add New App')}</Modal.Header>
      <form
        id={newProjectFormId}
        onSubmit={(e) => {
          e.preventDefault()
          handleEnter()
        }}
      >
        {step <= 2 ? (
          <Modal.Body className='modal-md'>
            {step === 1 && (
              <>
                <Field>
                  <Label>
                    <Trans>Name</Trans>
                  </Label>
                  <SimpleInput
                    id='org-detail-new-app-name'
                    required
                    placeholder={t('Please enter name')}
                    value={newProjectName}
                    onChange={handleChangeName}
                    type='text'
                    withCounterConfig={{
                      min: appConfig.charConstraints.min,
                      max: appConfig.charConstraints.max,
                    }}
                    error={nameError}
                  />
                </Field>
                <Field>
                  <Label>
                    <Trans>Team</Trans>
                  </Label>
                  <SelectDropdown
                    id='org-detail-new-app-team'
                    value={orgTeams?.find((ts) => ts.id === team) || orgTeams?.[0]}
                    onChange={(opt) => {
                      setTeam(opt.id)
                    }}
                    optionLabel={(opt) => opt.name}
                    renderOption={(opt) => {
                      return <AvatarCell size={AvatarSize.XS} {...opt} />
                    }}
                    options={orgTeams}
                  />
                </Field>
              </>
            )}
            {step === 2 && (
              <div className='grid grid-cols-2 gap-8 auto-rows-max'>
                <div className='flex flex-col'>
                  <div className='border-t border-x bdr rounded-t flex-cc px-8 py-4 font-bold'>
                    <Trans>Use Existing Database</Trans>
                  </div>
                  <div className='p-8 border bdr rounded-b flex flex-col gap-4 justify-center items-center flex-1'>
                    {filteredDb?.length === 0 ? (
                      <div className='red italic text-center'>
                        <Trans>No databases are assigned to the selected team.</Trans>
                      </div>
                    ) : (
                      <>
                        <div className='w-full'>
                          <DbDropdown
                            value={db}
                            onChange={(opt) => setDb(opt as DbItemAdapterV2)}
                            options={filteredDb ?? undefined}
                          />
                        </div>
                        <Button
                          className='w-full'
                          disabled={!db || isLoading}
                          onClick={() => {
                            if (db) {
                              handleCreate(onAdd, db.id)
                            }
                          }}
                        >
                          {isLoading && <span className='loading spin' />}
                          <Trans>Next</Trans>
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <div className='flex flex-col'>
                  <div className='border-t border-x bdr rounded-t flex-cc px-8 py-4 font-bold'>
                    <Trans>Create New Database</Trans>
                  </div>
                  <div className='w-full p-8 border bdr rounded-b flex flex-col gap-4 justify-center items-center flex-1'>
                    {canCreateDb ? (
                      <Button
                        className='w-full'
                        colorStyle={ButtonColor.Secondary}
                        disabled={!canCreateDb || isLoading}
                        onClick={onCreateDb}
                      >
                        <Trans>Next</Trans>
                      </Button>
                    ) : (
                      <div className='red italic text-center'>
                        <Trans>
                          You do not have permission to create new database, please contact your organization
                          administrator for permission.
                        </Trans>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        ) : (
          <CreateDbModal
            noWrap
            onNext={() => setStep((prev) => prev + 1)}
            onBack={(st) => {
              if (st <= 2) {
                setStep((prev) => prev - 1)
              }
            }}
            onFinally={() => {
              setStep(1)
              setExistingDb(false)
            }}
            onSubmit={(dbId) => {
              handleCreate(onAdd, dbId)
            }}
          />
        )}
        {step < 3 && (
          <Modal.Footer className={clsx(step === 1 && 'justify-end', step === 2 && 'justify-start')}>
            {step === 1 && (
              <Button
                variant={ButtonVariant.Outlined}
                colorStyle={ButtonColor.Tertiary}
                onClick={closeModal}
                className='mr-auto'
              >
                <Trans>Cancel</Trans>
              </Button>
            )}
            {step > 1 && (
              <Button variant={ButtonVariant.Outlined} colorStyle={ButtonColor.Tertiary} onClick={handleStepClick}>
                <Trans>Back</Trans>
              </Button>
            )}
            {step === 1 && (
              <SaveButton
                colorStyle={ButtonColor.Tertiary}
                id='org-detail-new-app-save'
                isLoading={isLoading}
                disabled={stepOneNextButtonDisabled}
                type='submit'
                label={<Trans>Next</Trans>}
              />
            )}
          </Modal.Footer>
        )}
      </form>
    </Modal>
  )
}

function AddNewAppModalWrapper({ onAdd, hideButton }: AddNewAppModalProps) {
  const { data: databases, isLoading: isDbLoading } = useGetOrgDatabases()
  const { isLoading: isTeamListLoading } = useGetOrgTeams()
  const { showAdd, setShowAdd } = useOrgAppsContext()

  return (
    <>
      <Button
        className={clsx('font-semibold', hideButton && 'hidden!')}
        onClick={() => setShowAdd(true)}
        icon={<Icon.Plus16 />}
      >
        <Trans>Add New App</Trans>
      </Button>

      {isDbLoading || isTeamListLoading ? (
        <Modal id='org-detail-new-app-modal' show={showAdd} onHide={() => setShowAdd(false)}>
          <Modal.Header onCloseClick={() => setShowAdd(false)}>
            <Trans>Add New App</Trans>
          </Modal.Header>
          <Modal.Body className='modal-md flex-cc min-h-[200px]'>
            <span className='loading' />
          </Modal.Body>
        </Modal>
      ) : (
        <AddNewAppModal onAdd={onAdd} databases={databases} />
      )}
    </>
  )
}

export default AddNewAppModalWrapper
